import { Divider, FormHelperText, MenuItem, Select, SelectChangeEvent, Stack, TextField } from '@mui/material'
import { C1Tooltip } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1LabeledSelectStyles } from 'components/core/RHKFormComponents/C1LabeledSelectStyles'
import { C1Select } from 'components/core/select/C1Select'
import { roundedDurationString } from 'components/core/time/duration/duration'
import { Info } from 'lucide-react'
import { Duration } from 'pbts/google/protobuf/duration'
import { ChangeEvent, ComponentProps, FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, FormattedPlural, IntlShape, useIntl } from 'react-intl'
import { DurationStyles } from './styles'

// Ensure logic and options in sync with:
// https://github.com/ductone/c1/blob/main/pkg/controller/slack/controller/modal_access_request.go

// Time constants in seconds
export const MINUTE = 60
export const HOUR = MINUTE * 60
export const DAY = HOUR * 24
export const WEEK = DAY * 7
export const MONTH = DAY * 30
// const YEAR = DAY * 365

const _second = 'second'
const _minute = 'minute'
const _hour = 'hour'
const _day = 'day'
const _week = 'week'
const _month = 'month'
type units = typeof _hour | typeof _day | typeof _week | typeof _month | typeof _second | typeof _minute
const units: Array<units> = ['second', 'minute', 'hour', 'day', 'week', 'month']

const unitToTime = (u: units, n: number): number => {
    switch (u) {
        case 'second':
            return n
        case 'minute':
            return MINUTE * n
        case 'month':
            return MONTH * n
        case 'week':
            return WEEK * n
        case 'day':
            return DAY * n
        case 'hour':
            return HOUR * n
        default:
            throw new Error('Invalid unit: ${u}')
    }
}

export enum periods {
    ONE_HOUR = HOUR,
    TWELVE_HOURS = HOUR * 12,
    ONE_DAY = DAY,
    ONE_WEEK = WEEK,
    TWO_WEEKS = WEEK * 2,
    ONE_MONTH = MONTH,
    INDEF = 0,
    CUSTOM = -1,
}

const defaultPeriods = [
    periods.ONE_HOUR,
    periods.TWELVE_HOURS,
    periods.ONE_DAY,
    periods.ONE_WEEK,
    periods.TWO_WEEKS,
    periods.ONE_MONTH,
]

const timeToPeriod: Record<number, periods> = {}
for (const [k, v] of Object.entries(periods)) {
    // NOTE: TS Enums should never be used
    if (typeof v === 'string') {
        continue
    }
    timeToPeriod[v] = periods[k]
}

const getUnitsToHuman = (intl: IntlShape): Record<units, { one: ReactNode, other: string }> => ({
    second: {
        one: <FormattedMessage id="duration_valid_unit_options_second_one" defaultMessage="second" />,
        other: intl.formatMessage({
            defaultMessage: 'seconds',
            id: 'duration_valid_unit_options_second_plural',
        }),
    },
    minute: {
        one: <FormattedMessage id="duration_valid_unit_options_minute_one" defaultMessage="minute" />,
        other: intl.formatMessage({
            defaultMessage: 'minutes',
            id: 'duration_valid_unit_options_minute_plural',
        }),
    },
    hour: {
        one: <FormattedMessage id="duration_valid_unit_options_hour_one" defaultMessage="hour" />,
        other: intl.formatMessage({
            defaultMessage: 'hours',
            id: 'duration_valid_unit_options_hour_plural',
        }),
    },
    day: {
        one: <FormattedMessage id="duration_valid_unit_options_day_one" defaultMessage="day" />,
        other: intl.formatMessage({
            defaultMessage: 'days',
            id: 'duration_valid_unit_options_day_plural',
        }),
    },
    week: {
        one: <FormattedMessage id="duration_valid_unit_options_week_one" defaultMessage="week" />,
        other: intl.formatMessage({
            defaultMessage: 'weeks',
            id: 'duration_valid_unit_options_week_plural',
        }),
    },
    month: {
        one: <FormattedMessage id="duration_valid_unit_options_month_one" defaultMessage="month" />,
        other: intl.formatMessage({
            defaultMessage: 'months',
            id: 'duration_valid_unit_options_month_plural',
        }),
    },
})

const validPeriodOptions = (intl: IntlShape, maxDuration: number = periods.INDEF, allowIndefiniteDuration?: boolean):
    Array<React.ReactElement> => {
    const unitsToHuman = getUnitsToHuman(intl)

    // get preset period options based on maxDuration
    const presets = defaultPeriods.filter((p) => maxDuration === periods.INDEF || p < maxDuration)

    // add max if maxDuration is set
    if (maxDuration !== periods.INDEF && allowIndefiniteDuration) {
        presets.push(maxDuration)
    }

    const a = presets.map((u) => {
        const customUnit = customUnitFromDuration(u)
        const unitTime = unitToTime(customUnit, 1)
        const numberOfThem = u / unitTime

        return <MenuItem key={`duration_select_item_${u}`} value={u}>
            {numberOfThem}
            &nbsp;
            <FormattedPlural {...unitsToHuman[customUnit]} value={numberOfThem} />
        </MenuItem>
    })

    // add indefinite if maxDuration is not set
    if (maxDuration === periods.INDEF && allowIndefiniteDuration) {
        a.push(<MenuItem key={`duration_select_item_${periods.INDEF}`} value={periods.INDEF}>
            <FormattedMessage id="duration_form_period_label_one_month" defaultMessage="Indefinite" />
        </MenuItem>)
    }

    // add custom
    a.push(<MenuItem key={`duration_select_item_${periods.CUSTOM}`} value={periods.CUSTOM}>
        <FormattedMessage id="duration_form_period_label_custom" defaultMessage="Custom" />
    </MenuItem>)

    a.splice(a.length - 1, 0, <Divider key="divider" style={{ marginTop: 0, marginBottom: 0 }} />)
    return a
}

const validUnitsOptions = (intl: IntlShape, numberOfThem: number, minDuration: number, maxDuration?: number): Array<React.ReactElement> => {
    const unitsToHuman = getUnitsToHuman(intl)

    return units.filter((u) => {
        const n = unitToTime(u, 1)
        if (n < minDuration) {
            return false
        }
        if (!maxDuration) {
            return true
        }
        if (n === periods.INDEF) {
            return false
        }
        return n <= maxDuration
    }).map((u) => <MenuItem key={`unit_item_${u}`} value={u}>
        <FormattedPlural {...unitsToHuman[u]} value={numberOfThem} />
    </MenuItem>)
}

function isDivisibleAndNotZero(dividend: number, divisor: number): boolean {
    return dividend % divisor === 0 && dividend !== 0
}

// choose largest evenly divisible unit
export const customUnitFromDuration = (duration: number): units => (
    isDivisibleAndNotZero(duration, MONTH)
        ? 'month'
        : isDivisibleAndNotZero(duration, WEEK)
            ? 'week'
            : isDivisibleAndNotZero(duration, DAY)
                ? 'day'
                : 'hour')

export const customExtendedUnitFromDuration = (duration: number): units => (
    isDivisibleAndNotZero(duration, MONTH)
        ? 'month'
        : isDivisibleAndNotZero(duration, WEEK)
            ? 'week'
            : isDivisibleAndNotZero(duration, DAY)
                ? 'day'
                : isDivisibleAndNotZero(duration, HOUR)
                    ? 'hour'
                    : isDivisibleAndNotZero(duration, MINUTE)
                        ? 'minute'
                        : 'second')

const validateTime = (time: number, intl: IntlShape, maxDuration?: number): undefined | string => {
    // scalar is a number
    if (isNaN(time)) {
        return intl.formatMessage({
            defaultMessage: 'Duration must be a number',
            id: 'duration_select_validate_1',
        })
    }

    // scalar is an integer
    if (!Number.isInteger(time)) {
        return intl.formatMessage({
            defaultMessage: 'Duration must be an integer',
            id: 'duration_select_validate_2',
        })
    }

    // scalar is > 0
    if (time < 0) {
        return intl.formatMessage({
            defaultMessage: 'Duration must be greater than zero',
            id: 'duration_select_validate_3',
        })
    }

    // scalar is safe
    if (time >= Number.MAX_SAFE_INTEGER) {
        return intl.formatMessage(
            {
                defaultMessage: 'Duration may not exceed {max_safe_integer}',
                id: 'duration_select_validate_4',
            },
            {
                max_safe_integer: roundedDurationString(
                    intl,
                    Duration.create({
                        seconds: Number.MAX_SAFE_INTEGER,
                    }),
                ),
            },
        )
    }

    // scalar is < maxDuration
    if (maxDuration && (time === periods.INDEF || time > maxDuration)) {
        return intl.formatMessage(
            {
                defaultMessage: 'Duration may not exceed {max_duration}',
                id: 'duration_select_validate_5',
            },
            {
                max_duration: roundedDurationString(
                    intl,
                    Duration.create({
                        seconds: maxDuration,
                    }),
                ),
            },
        )
    }

    return undefined
}

export type DurationSelectProps = {
    onChange: (seconds: number) => void
    value?: number
    defaultValue?: number
    maxDuration?: number
    disabled?: boolean
    isCampaignDurationEnabled?: boolean
    isGrantDurationEnabled?: boolean
    fullWidth?: boolean
    isWorkflowDurationEnabled?: boolean
    allowIndefiniteDuration?: boolean
} & Omit<ComponentProps<typeof Select>, 'onChange' | 'value' | 'disabled'>

export const DurationSelect: FC<DurationSelectProps> = ({
    onChange,
    value,
    defaultValue: defaultValue_,
    maxDuration,
    fullWidth = false,
    disabled = false,
    isCampaignDurationEnabled = false,
    isGrantDurationEnabled = false,
    isWorkflowDurationEnabled = false,
    allowIndefiniteDuration = true,
    ...rest
}) => {
    const intl = useIntl()
    const defaultValue = value !== undefined
        ? value
        : defaultValue_ !== undefined
            ? defaultValue_
            : Math.min(maxDuration || WEEK, WEEK)

    const [helperText, setHelperText] = useState<string | undefined>(() => validateTime(defaultValue, intl, maxDuration))
    const [selectedPeriod, setSelectedPeriod] = useState<periods>(() => {
        if (defaultValue in periods) {
            return defaultValue
        }
        if (isCampaignDurationEnabled) {
            return defaultValue
        }
        return periods.CUSTOM
    })
    const [selectedUnit, setSelectedUnit] = useState<units>(isWorkflowDurationEnabled
        ? customExtendedUnitFromDuration(defaultValue) : customUnitFromDuration(defaultValue))
    const [selectedScalar, setSelectedScalar] = useState<number>(() => {
        const u = isWorkflowDurationEnabled ? customExtendedUnitFromDuration(defaultValue) : customUnitFromDuration(defaultValue)
        const t = unitToTime(u, 1)
        return defaultValue / t
    })
    const previousPeriod = useRef(selectedPeriod)
    // For controlled inputs
    useEffect(() => {
        if (value === undefined) {
            return
        }
        if (value < 0) {
            throw new Error('Invalid value for a duration: ${value}')
        }

        // derive selected stuff!!
        const unit = isWorkflowDurationEnabled ? customExtendedUnitFromDuration(value) : customUnitFromDuration(value)
        const s = value / unitToTime(unit, 1)

        if (!isFinite(s)) {
            throw new Error('non finite value for a duration: ${s} (${value})')
        }

        setHelperText(validateTime(value, intl, maxDuration))

        const pp = previousPeriod.current
        previousPeriod.current = selectedPeriod
        // maintain the custom selections
        if (selectedPeriod === periods.CUSTOM) {
            // If we aren't switching over to custom, maintain the units
            //  so from 31 -> 30 days doesn't turn into 1 month!
            if (pp === periods.CUSTOM) {
                const maybeS = value / unitToTime(selectedUnit, 1)
                // sometimes controlled inputs will just send us a new value
                //   try to do the right thing I guess!
                if (Number.isInteger(maybeS)) {
                    setSelectedScalar(maybeS)
                    return
                }
            }
            setSelectedUnit(unit)
            setSelectedScalar(s)
            return
        }

        // try to set the correct period
        const period = timeToPeriod[value]
        if (period !== undefined) {
            setSelectedPeriod(period)
            return
        }

        // if not preset duration option, check if it is maxDuration
        if (value === maxDuration) {
            setSelectedPeriod(maxDuration)
            return
        }

        // if not preset or maxDuration, set to custom
        if (!isCampaignDurationEnabled) {
            setSelectedPeriod(periods.CUSTOM)
        }
        setSelectedUnit(unit)
        setSelectedScalar(s)
    }, [value, selectedUnit, selectedPeriod, intl, maxDuration, isCampaignDurationEnabled, isWorkflowDurationEnabled])

    const { periodOptions, unitOptions } = useMemo(() => ({
        periodOptions: validPeriodOptions(intl, maxDuration, allowIndefiniteDuration),
        unitOptions: isWorkflowDurationEnabled ?
            validUnitsOptions(intl, selectedScalar, 0, maxDuration) :
            validUnitsOptions(intl, selectedScalar, periods.ONE_HOUR, maxDuration),
    }), [intl, selectedScalar, maxDuration, isWorkflowDurationEnabled, allowIndefiniteDuration])

    const templatePeriodOptions = useMemo(() => {
        const templateSpecificPeriods = defaultPeriods.slice(3)
        templateSpecificPeriods.push(periods.ONE_WEEK * 3)
        templateSpecificPeriods.sort((a, b) => a - b)
        const presets = templateSpecificPeriods

        return presets.map((u) => <MenuItem key={`duration_select_item_${u}`} value={u}>
            {roundedDurationString(intl, Duration.create({ seconds: u }))}
        </MenuItem>)
    }, [intl])

    const grantPeriodOptions = useMemo(() => {
        const grantSpecificPeriods = defaultPeriods.slice(3)
        grantSpecificPeriods.push(periods.ONE_MONTH * 3)
        const presets = grantSpecificPeriods.map((u) => <MenuItem key={`duration_select_item_${u}`} value={u}>
            {roundedDurationString(intl, Duration.create({ seconds: u }))}
        </MenuItem>)

        // add custom
        presets.push(<MenuItem key={`duration_select_item_${periods.CUSTOM}`} value={periods.CUSTOM}>
            <FormattedMessage id="duration_form_period_label_custom" defaultMessage="Custom" />
        </MenuItem>)

        if (maxDuration === periods.INDEF && allowIndefiniteDuration) {
            presets.push(<MenuItem key={`duration_select_item_${periods.INDEF}`} value={periods.INDEF}>
                <FormattedMessage id="duration_form_period_label_indefinite" defaultMessage="Indefinite" />
            </MenuItem>)
        }

        presets.splice(presets.length - 1, 0, <Divider key="divider" style={{ marginTop: 0, marginBottom: 0 }} />)

        return presets
    }, [intl, maxDuration, allowIndefiniteDuration])

    const onPeriodChange = (e: SelectChangeEvent<periods>): void => {
        setHelperText(undefined)
        const p = e.target.value as periods
        if (p === undefined) {
            throw new Error('Invalid period (undefined).  This should not be possible')
        }

        // no validation necessary for preset duration Select
        setSelectedPeriod(p)

        if (p !== periods.CUSTOM) {
            onChange(p)
            return
        }
        // set correct unit & scalar for the current value
        //  NOTE: this should't change the value!
        const v = value || selectedPeriod
        const unit = isWorkflowDurationEnabled ? customExtendedUnitFromDuration(v) : customUnitFromDuration(v)

        const s = v / unitToTime(unit, 1)
        if (isFinite(s)) {
            setSelectedUnit(unit)
            setSelectedScalar(s)
        }
    }

    const onScalarChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setHelperText(undefined)

        const scalarText = e.target.value
        if (!scalarText) {
            setHelperText(undefined)
            return
        }
        const scalarVal = Number(scalarText)
        setSelectedScalar(scalarVal)
        const t = unitToTime(selectedUnit, scalarVal)
        setHelperText(validateTime(t, intl, maxDuration))
        onChange(t)
    }

    const onUnitChange = (e: SelectChangeEvent<units>): void => {
        setHelperText(undefined)

        const _unit = e.target.value as units
        setSelectedUnit(_unit)
        const t = unitToTime(_unit, selectedScalar)
        setHelperText(validateTime(t, intl, maxDuration))
        onChange(t)
    }

    if (isWorkflowDurationEnabled) {
        return <Stack direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                    value={selectedScalar}
                    onChange={onScalarChange}
                    type="number"
                    size="small"
                    inputProps={{ min: 1 }}
                    error={!!helperText}
                    sx={DurationStyles.numberInput}
                    aria-label="duration-scalar"
                    fullWidth
                />
                <Select<units>
                    value={selectedUnit}
                    onChange={onUnitChange}
                    error={!!helperText}
                    sx={C1LabeledSelectStyles.selectOverrides}
                    aria-label="duration-unit"
                    size="small"
                    fullWidth
                >
                    {unitOptions}
                </Select>
            </Stack>
            {helperText && <FormHelperText sx={DurationStyles.error}>{helperText}</FormHelperText>}
        </Stack>
    }

    return <Stack direction="column" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
            <C1Select<periods>
                value={selectedPeriod}
                onChange={onPeriodChange}
                sx={C1LabeledSelectStyles.selectOverrides}
                size="small"
                disabled={disabled}
                fullWidth={fullWidth}
                labelId={rest.id ? `label-${rest.id}` : undefined}
                aria-label={rest.id ? undefined : 'duration-select'}
                {...rest}
            >
                {isCampaignDurationEnabled ? templatePeriodOptions : isGrantDurationEnabled ? grantPeriodOptions : periodOptions}
            </C1Select>
            {selectedPeriod === periods.CUSTOM && <TextField
                value={selectedScalar}
                onChange={onScalarChange}
                type="number"
                size="small"
                inputProps={{ min: 1 }}
                error={!!helperText}
                sx={{ width: '15ch' }}
                InputProps={{
                    sx: { '.MuiInputBase-input': { height: '15px' } },
                }}
                aria-label="duration-scalar"
                disabled={disabled}
            />}
            {selectedPeriod === periods.CUSTOM && <C1Select<units>
                value={selectedUnit}
                onChange={onUnitChange}
                error={!!helperText}
                sx={C1LabeledSelectStyles.selectOverrides}
                aria-label="duration-unit"
                size="small"
                disabled={disabled}
            >
                {unitOptions}
            </C1Select>}
        </Stack>
        {helperText && <FormHelperText sx={DurationStyles.error}>{helperText}</FormHelperText>}
    </Stack>
}

export const MaxGrantDurationTooltip: FC = () => {
    const intl = useIntl()
    return <C1Tooltip title={intl.formatMessage({
        id: 'max_request_duration_tooltip',
        defaultMessage: 'Limits the maximum time a user can request this entitlement for.',
    })}
    >
        <C1Icon icon={Info} />
    </C1Tooltip>
}
